import { Drawer, Loader, Stack } from '@mantine/core';
import { IconFileStack, IconReceipt } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IOutsourcedOrder, OutsourcedStatus } from '../../hooks/outsourcingOrders/interfaces';
import { IStore } from '../../interfaces/Store';
import { getOutsourcedOrdersByOrder } from '../../services/outsourcingOrder.service';
import { showNotification } from '../../utils/Notification/Notification';
import {
  getOutsourcedOrdersForSamePrintGroup,
  resolveOutsourcedOrderEntityId,
  tryPrintConsolidatedOutsourcedOrderTicket,
  tryPrintOutsourcedOrderTicket
} from '../../utils/outsourcedOrderTicketPrint';
import DrawerActionCard from '../DrawerActionCard';

interface OutsourcedOrderPrintTicketDrawerProps {
  opened: boolean;
  onClose: () => void;
  outsourcedOrder: IOutsourcedOrder | null;
  store?: IStore | null;
  companyLogoUrl?: string;
}

type PrintMode = 'consolidated' | 'individual';

const ACTIVE_OUTSOURCED_TICKET_STATUSES: OutsourcedStatus[] = ['pending', 'in_process'];

function extractOutsourcedOrdersFromResponse(response: any): IOutsourcedOrder[] {
  const data = response?.data?.data;
  return Array.isArray(data) ? data : [];
}

const OutsourcedOrderPrintTicketDrawer = ({
  opened,
  onClose,
  outsourcedOrder,
  store,
  companyLogoUrl
}: OutsourcedOrderPrintTicketDrawerProps) => {
  const { t } = useTranslation(['OutsourcingPage']);
  const [printingMode, setPrintingMode] = useState<PrintMode | null>(null);
  const isDisabled = !outsourcedOrder || Boolean(printingMode);

  const canPrintWithSelectedStore = () => {
    if (!outsourcedOrder || !store?._id) {
      showNotification({
        type: 'Error',
        title: t('OutsourcingPage:printTicket.storeLoadError')
      });
      return false;
    }

    if (resolveOutsourcedOrderEntityId(outsourcedOrder.store) !== store._id) {
      showNotification({
        type: 'Error',
        title: t('OutsourcingPage:printTicket.storeLoadError')
      });
      return false;
    }

    return true;
  };

  const onPrintIndividual = async () => {
    if (!outsourcedOrder) return;
    if (!canPrintWithSelectedStore()) return;
    setPrintingMode('individual');
    try {
      await tryPrintOutsourcedOrderTicket({ outsourcedOrder, store, companyLogoUrl, t });
      onClose();
    } finally {
      setPrintingMode(null);
    }
  };

  const onPrintConsolidated = async () => {
    if (!outsourcedOrder) return;
    if (!canPrintWithSelectedStore()) return;
    const orderId = resolveOutsourcedOrderEntityId(outsourcedOrder.order);

    if (!orderId) {
      await onPrintIndividual();
      return;
    }

    setPrintingMode('consolidated');
    try {
      const partnerId = resolveOutsourcedOrderEntityId(outsourcedOrder.partner);
      const response = await getOutsourcedOrdersByOrder(orderId, {
        partnerId: partnerId || undefined,
        statuses: ACTIVE_OUTSOURCED_TICKET_STATUSES
      });
      const sameGroupOutsourcedOrders = getOutsourcedOrdersForSamePrintGroup(
        outsourcedOrder,
        extractOutsourcedOrdersFromResponse(response)
      );

      await tryPrintConsolidatedOutsourcedOrderTicket({
        outsourcedOrder,
        outsourcedOrders: sameGroupOutsourcedOrders.length
          ? sameGroupOutsourcedOrders
          : [outsourcedOrder],
        store,
        companyLogoUrl,
        t
      });
      onClose();
    } catch (error) {
      showNotification({
        type: 'Error',
        title: t('OutsourcingPage:printTicket.groupLoadError')
      });
    } finally {
      setPrintingMode(null);
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      title={t('OutsourcingPage:printOutsourcedOrderTicket')}
      padding="md"
      size="sm">
      <Stack gap="sm">
        <DrawerActionCard
          label={t('OutsourcingPage:printConsolidatedOutsourcedOrderTicket')}
          description={t('OutsourcingPage:printConsolidatedOutsourcedOrderTicketDescription')}
          icon={<IconFileStack />}
          iconColor="blue"
          onClick={onPrintConsolidated}
          disabled={isDisabled}
          ariaLabel={t('OutsourcingPage:printConsolidatedOutsourcedOrderTicket')}
          rightSection={printingMode === 'consolidated' ? <Loader size="sm" /> : undefined}
        />
        <DrawerActionCard
          label={t('OutsourcingPage:printIndividualOutsourcedOrderTicket')}
          description={t('OutsourcingPage:printIndividualOutsourcedOrderTicketDescription')}
          icon={<IconReceipt />}
          iconColor="teal"
          onClick={onPrintIndividual}
          disabled={isDisabled}
          ariaLabel={t('OutsourcingPage:printIndividualOutsourcedOrderTicket')}
          rightSection={printingMode === 'individual' ? <Loader size="sm" /> : undefined}
        />
      </Stack>
    </Drawer>
  );
};

export default OutsourcedOrderPrintTicketDrawer;
